<template>
  <VueFinalModal
    :click-to-close="clickToClose"
    :hide-overlay="hideOverlay"
    :focus-trap="focusTrap"
    class="modal__container"
    overlay-class="!fixed !bg-slate-900/60 dark:!bg-zinc-900/60"
    :content-class="contentClass()"
    :overlay-transition="{ css: false }"
    @click-outside="onClickOutside"
    @before-open="lockDocumentScroll"
    @closed="unlockDocumentScroll"
  >
    <div class="w-full dark:text-zinc-300" :data-testid="dataTestid">
      <button
        v-if="!hideClose"
        @click.stop.prevent="$emit('close')"
        data-testid="modal-close"
        class="absolute rounded-full z-10"
        :class="[closeButtonClasses, closeButtonThemeClasses]"
      >
        <Icon name="general.crossLight" size="base" />
      </button>

      <div class="modal__content">
        <slot />
      </div>
    </div>
  </VueFinalModal>
</template>

<script lang="ts">
const DOCUMENT_SCROLL_LOCK_CLASS = 'modal-scroll-locked';
const documentScrollLockOwners = new Set<symbol>();
</script>

<script lang="ts" setup>
import { onBeforeUnmount } from 'vue';
import { VueFinalModal } from 'vue-final-modal';
import { Icon } from './../icon';

export interface ConfirmModalProps {
  dataTestid?: string;
  hideClose?: boolean;
  hideOverlay?: boolean;
  clickToClose?: boolean;
  onClickOutside?: () => void;
  noBorder?: boolean;
  closeButtonInset?: 'default' | 'large';
  closeButtonTheme?: 'default' | 'light';
}

const props = withDefaults(defineProps<ConfirmModalProps>(), { clickToClose: true, closeButtonInset: 'default', closeButtonTheme: 'default' });

// Triggers are often non-focusable <a> Buttons, so focus returns to a tall ancestor; scrolling it into view jumps the page.
const focusTrap = { allowOutsideClick: true, preventScroll: true };

const closeButtonClasses = {
  'p-1 top-4 right-4': props.closeButtonInset === 'default',
  'p-2 top-8 right-6': props.closeButtonInset === 'large',
};

const closeButtonThemeClasses = {
  'bg-white text-slate-700 dark:bg-zinc-800 dark:text-zinc-300 hover:bg-slate-100 dark:hover:bg-zinc-700 dark:hover:text-zinc-200':
    props.closeButtonTheme === 'default',
  'bg-white text-slate-700 hover:bg-slate-100': props.closeButtonTheme === 'light',
};

// vue-final-modal locks scroll on <body> only. When <html> has its own overflow (the viewer layout sets
// overflow-x: clip), body's overflow no longer reaches the viewport and the page behind the modal scrolls.
const documentScrollLockOwner = Symbol('modal');

const syncDocumentScrollLock = () => {
  document.documentElement.classList.toggle(DOCUMENT_SCROLL_LOCK_CLASS, documentScrollLockOwners.size > 0);
};

const lockDocumentScroll = () => {
  documentScrollLockOwners.add(documentScrollLockOwner);
  syncDocumentScrollLock();
};

const unlockDocumentScroll = () => {
  documentScrollLockOwners.delete(documentScrollLockOwner);
  syncDocumentScrollLock();
};

onBeforeUnmount(unlockDocumentScroll);

const contentClass = () => {
  return props.noBorder ? 'modal no-border' : 'modal';
};
</script>
